import {acceptParentMessage, postToParent} from "./IframeParentOrigin.js";

// Message types - Child -> Parent
export interface IframeResizeMessage {
    type: 'IframeResize';
    height: number;
}

export interface IframeLocationChangeMessage {
    type: 'IframeLocationChange';
    url: string;
    title: string;
}

export interface IframeNavigatingMessage {
    type: 'IframeNavigating';
}

export interface ScrollToTopMessage {
    type: 'PeakIDXScrollToTop';
}

// Message types - Parent -> Child
export interface NavigateToPageMessage {
    type: 'NavigateToPage';
    url: string;
}

export interface WindowHeightMessage {
    type: 'PeakIDXWindowHeight';
    height: number;
    iframeScrollY: number;
}

export class IframeChildManager {
    private lastHeight: number = 0;
    private lastUrl: string = '';
    private lastTitle: string = '';
    private isTouchActive = false;
    private heightPollInterval: number | null = null;
    private containerElement: HTMLElement | null = null;
    private modalSelector: string = "";
    private onNavigateCallback: ((url: string) => void) | null = null;
    private debug: boolean = true;

    constructor(containerElement: HTMLElement | null, modalSelector: string = "") {
        this.containerElement = containerElement;
        this.modalSelector = modalSelector;
        this.setupTouchListeners();
        this.setupMessageListener();
        this.setupResizeObserver();
        this.log('IframeChildManager initialized');
    }

    private log(msg: string, ...args: any[]): void {
        if (this.debug) {
            console.log(`[IframeChild] ${msg}`, ...args);
        }
    }

    private setupTouchListeners(): void {
        window.addEventListener('touchstart', () => {
            this.isTouchActive = true;
        }, { passive: true });

        window.addEventListener('touchend', () => {
            this.isTouchActive = false;
            this.reportHeight();
        }, { passive: true });
    }

    private setupMessageListener(): void {
        window.addEventListener('message', (e: MessageEvent) => {
            if (!acceptParentMessage(e)) return;
            const data = e.data;
            if (data && data.type === 'PeakIDXHostReady') {
                this.lastHeight = 0;
                this.reportHeight();
                postToParent({type: 'IframeLocationChange', url: window.location.href, title: document.title});
            }

            // Log all messages for debugging
            // if (data && data.type) {
            //     this.log('Received message:', data.type, data);
            // }

            // Handle parent-initiated navigation (back/forward button)
            if (data && data.type === 'NavigateToPage' && typeof data.url === 'string') {
                const message = data as NavigateToPageMessage;
                this.log('Processing NavigateToPage:', message.url);

                if (this.onNavigateCallback) {
                    this.log('Calling onNavigateCallback');
                    this.onNavigateCallback(message.url);
                } else {
                    console.warn('[IframeChild] NavigateToPage received but no callback set!');
                }
            }

            // Note: WindowHeightMessage is handled separately in base.ts
            // for scroll sync with fixed elements like maps
        });
    }

    private setupResizeObserver(): void {
        if (this.containerElement) {
            const observer = new ResizeObserver(() => this.reportHeight());
            observer.observe(this.containerElement);
        }
    }

    setNavigateCallback(callback: (url: string) => void): void {
        this.log('setNavigateCallback called');
        this.onNavigateCallback = callback;
    }

    setContainerElement(element: HTMLElement): void {
        this.containerElement = element;
        this.setupResizeObserver();
    }

    private getHeight(): number {
        if (!this.containerElement) return document.body.scrollHeight;

        let height = Math.round(this.containerElement.getBoundingClientRect().height);

        if (this.modalSelector) {
            const modalDiv = document.querySelector(this.modalSelector) as HTMLElement;
            if (modalDiv && modalDiv.offsetParent !== null) {
                height = Math.round(modalDiv.getBoundingClientRect().height + 50);
            }
        }

        return height;
    }

    // Report height changes to parent (separate from location)
    reportHeight(): void {
        if (this.isTouchActive) return;

        const scheduler = (window as any).requestIdleCallback || ((cb: any) => setTimeout(cb, 1));

        scheduler(() => {
            const height = this.getHeight();

            // Only report if height changed significantly
            if (Math.abs(this.lastHeight - height) < 5) {
                return;
            }

            this.lastHeight = height;

            const message: IframeResizeMessage = {
                type: 'IframeResize',
                height: height
            };

            postToParent(message);
        }, { timeout: 200 });
    }

    // Report location/title changes to parent (separate from height)
    reportLocation(url: string, title: string): void {
        // Only report if changed
        if (this.lastUrl === url && this.lastTitle === title) {
            return;
        }

        this.lastUrl = url;
        this.lastTitle = title;

        const message: IframeLocationChangeMessage = {
            type: 'IframeLocationChange',
            url: url,
            title: title
        };

        postToParent(message);
    }

    // Notify parent that navigation is about to happen
    // Parent should save scroll position and scroll to top
    notifyNavigating(): void {
        const message: IframeNavigatingMessage = {
            type: 'IframeNavigating'
        };
        postToParent(message);
    }

    // Called for user-initiated navigation in iframe
    // Uses replaceState - parent handles pushState for browser history
    navigate(url: string, title: string): void {
        // Notify parent before navigation
        this.notifyNavigating();

        // Use replaceState, not pushState - parent manages browser history
        console.log("IframeChildManager navigate (replaceState):", url);
        history.replaceState({ url }, title, url);

        // Report the new location to parent
        this.reportLocation(url, title);

        // Start polling for height stability
        this.startHeightPolling();
    }

    // Called when parent navigates us (back/forward)
    // Just update URL state without notifying parent
    handleParentNavigation(url: string, title: string): void {
        console.log("IframeChildManager handleParentNavigation:", url);
        // history.replaceState({ url }, title, url);
        this.lastUrl = url;
        this.lastTitle = title;
        this.startHeightPolling();
    }

    startHeightPolling(): void {
        this.stopHeightPolling();

        let stableCount = 0;
        let lastHeight = 0;

        this.heightPollInterval = window.setInterval(() => {
            const height = this.getHeight();
            this.reportHeight();

            if (height === lastHeight) {
                stableCount++;
                if (stableCount >= 5) {
                    this.stopHeightPolling();
                }
            } else {
                stableCount = 0;
                lastHeight = height;
            }
        }, 150);

        // Safety timeout
        setTimeout(() => this.stopHeightPolling(), 5000);
    }

    stopHeightPolling(): void {
        if (this.heightPollInterval) {
            clearInterval(this.heightPollInterval);
            this.heightPollInterval = null;
        }
    }

    scrollToTop(): void {
        const message: ScrollToTopMessage = {
            type: 'PeakIDXScrollToTop'
        };
        postToParent(message);
    }
}
